// @refresh reload

import { render } from "solid-js/web"
import { AppBaseProviders, AppInterface } from "@/app"
import { type Platform, PlatformProvider } from "@/context/platform"
import { normalizeServerUrl, ServerConnection } from "@/context/server"
import { handleNotificationClick } from "@/utils/notification-click"
import pkg from "@/../package.json"

const DEFAULT_SERVER_URL_KEY = "opencode.settings.dat:defaultServerUrl"

function getStorage(key: string) {
  if (typeof localStorage === "undefined") return null
  try { return localStorage.getItem(key) } catch { return null }
}
function setStorage(key: string, value: string | null) {
  if (typeof localStorage === "undefined") return
  try { value !== null ? localStorage.setItem(key, value) : localStorage.removeItem(key) } catch {}
}

function getCurrentUrl() {
  if (location.hostname.includes("opencode.ai")) return "http://localhost:4096"
  if (import.meta.env.DEV)
    return `http://${import.meta.env.VITE_OPENCODE_SERVER_HOST ?? "localhost"}:${import.meta.env.VITE_OPENCODE_SERVER_PORT ?? "4096"}`
  return location.origin
}

const platform: Platform = (() => {
  return {
    platform: "web",
    version: pkg.version,
    openLink: (url) => window.open(url, "_blank"),
    back: () => window.history.back(),
    forward: () => window.history.forward(),
    restart: async () => window.location.reload(),
    notify: async (title, desc, href) => {
      if (!("Notification" in window)) return
      const perm =
        Notification.permission === "default"
          ? await Notification.requestPermission().catch(() => "denied")
          : Notification.permission
      if (perm !== "granted") return
      if (document.visibilityState === "visible" && document.hasFocus()) return
      const n = new Notification(title, { body: desc ?? "", icon: "/favicon.ico" })
      n.onclick = () => {
        handleNotificationClick(href)
        n.close()
      }
    },
    getDefaultServer: async () => {
      const stored = getStorage(DEFAULT_SERVER_URL_KEY)
      return stored ? ServerConnection.Key.make(stored) : null
    },
    setDefaultServer: (url) => setStorage(DEFAULT_SERVER_URL_KEY, url),
  }
})()

function resolveServerConfig() {
  const url = import.meta.env.VITE_ONEFPGA_SERVER_URL as string | undefined
  const username = import.meta.env.VITE_ONEFPGA_SERVER_USERNAME as string | undefined
  const password = import.meta.env.VITE_ONEFPGA_SERVER_PASSWORD as string | undefined
  if (url && username && password !== undefined) {
    return { baseUrl: url, username, password }
  }
  return null
}

const root = document.getElementById("root")

if (root instanceof HTMLElement) {
  const config = resolveServerConfig()

  const servers: ServerConnection.Http[] = []
  let canonicalServer: ServerConnection.Http

  if (config) {
    const url = normalizeServerUrl(config.baseUrl) ?? config.baseUrl
    canonicalServer = {
      type: "http",
      authToken: !!config.password,
      http: {
        url,
        username: config.username || "opencode",
        password: config.password || undefined,
      },
    }
    servers.push(canonicalServer)
    setStorage(DEFAULT_SERVER_URL_KEY, url)
  } else {
    const currentUrl = getCurrentUrl()
    canonicalServer = {
      type: "http",
      authToken: false,
      http: { url: currentUrl, username: "opencode" },
    }
    servers.push(canonicalServer)
  }

  render(
    () => (
      <PlatformProvider value={platform}>
        <AppBaseProviders>
          <AppInterface
            defaultServer={ServerConnection.Key.make(
              getStorage(DEFAULT_SERVER_URL_KEY) ?? getCurrentUrl(),
            )}
            canonicalLocalServer={ServerConnection.key(canonicalServer)}
            servers={servers}
            disableHealthCheck
          />
        </AppBaseProviders>
      </PlatformProvider>
    ),
    root,
  )
}
